import { useRouteError, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { useMainTabs } from "@/hooks";
import { Button } from "@/components";
import { useTranslate } from "@/providers/context";
import { useEffect } from "react";

interface RouteError {
  statusText?: string;
  message?: string;
  status?: number;
}

export const ErrorPage = ({ code }: { code?: number }) => {
  const navigate = useNavigate();
  const { translate } = useTranslate();
  const { sortedClickableMainTabs } = useMainTabs();

  const firstTab = sortedClickableMainTabs?.[0];

  const error = useRouteError() as RouteError;
  console.error(error);

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  if (code === 404 || error.status === 404) {
    return (
      <div
        id="error-page"
        className="text-item-primary flex h-full w-full flex-col items-center justify-center text-center font-bold"
      >
        <div className="p1-bold text-item-tertiary mb-6">404</div>
        <div className="mb-3 text-[28px] font-bold leading-[38px]">
          {translate("Page not found")}
        </div>
        <div className="p1-regular mb-6">
          {translate("We couldn't find the page you're looking for.")}
        </div>
        <Button onClick={() => navigate("/", { replace: true })}>
          {translate("Return To")} {`${firstTab?.Name ?? translate("Home")}`}
        </Button>
      </div>
    );
  }

  return (
    <div
      id="error-page"
      className="text-item-primary flex h-full w-full items-center justify-center text-center font-bold"
    >
      <div>
        <h1 className="p-4 text-4xl">{translate("Oops!")}</h1>
        <p className="pb-2">
          {translate("Sorry, an unexpected error has occurred.")}
        </p>
        <p>
          <i>{translate(error.statusText || error.message || "")}</i>
        </p>
      </div>
    </div>
  );
};

// Used by GlobalLoadingWrapper (outside of the router) when properties/translations fail to load
export const GenericErrorPage = () => {
  const { translate } = useTranslate();
  return (
    <div
      id="error-page"
      className="text-item-primary flex h-full w-full items-center justify-center text-center font-bold"
    >
      <div>
        <h1 className="p-4 text-4xl">{translate("Oops!")}</h1>
        <p className="pb-2">
          {translate("Sorry, an unexpected error has occurred.")}
        </p>
      </div>
    </div>
  );
};
